/**
 * 페이지 배치 단위(= 캡처 단위)와 그 크기 환산.
 *
 * 미리보기에 페이지 경계를 그리려면 미리보기도 "무엇을 어떤 크기로 배치할지"를 알아야 한다.
 * 캡처 쪽과 따로 계산하면 반드시 어긋나므로(WEB-2264) 단위 정의와 mm 환산은 여기 하나만 안다.
 */

import { CHUNK_TYPE, ChunkType } from "@/app/report/common/export/paginate"

/**
 * 한 덩어리로 캡처할 요소들을 고른다.
 *
 * sectionIndex 0(= 리포트 상단 요약)은 통째로 한 장이고, 그 뒤 컨테이너들은 내부 섹션 단위로,
 * 표 컨테이너는 개별 table 단위로 쪼갠다. table 에는 식별용 클래스를 붙인다 —
 * 이후 크기 환산이 "표는 실측 폭 비례" 분기를 타야 하기 때문이다(WEB-2276).
 */
export const getHtmlTargets = (html: HTMLElement, sectionIndex?: number): HTMLElement[] => {
  if (sectionIndex === 0 || sectionIndex === undefined || sectionIndex === null) {
    return [html]
  }

  if (html.classList.contains("table-container")) {
    const tables = Array.from(html.querySelectorAll(":scope table"))
    if (tables.length > 0) {
      tables.forEach((table) => {
        ;(table as HTMLElement).classList.add("extracted-individual-table")
      })

      return tables as HTMLElement[]
    }

    return [html]
  }

  if (html.classList.contains("renewal-pdf-container")) {
    const tableContainer = html.querySelector(".table-container")
    if (tableContainer) {
      const tables = Array.from(tableContainer.querySelectorAll(":scope table"))
      if (tables.length > 0) {
        tables.forEach((table) => {
          ;(table as HTMLElement).classList.add("extracted-individual-table")
        })

        return tables as HTMLElement[]
      }
    }

    const innerSections = Array.from(html.querySelectorAll(":scope > .section"))
    if (innerSections.length > 0) {
      return innerSections as HTMLElement[]
    }

    return [html]
  }

  let targets = Array.from(html.querySelectorAll(".section.renewal-pdf"))
  if (targets.length === 0) {
    targets = Array.from(html.querySelectorAll(".section"))
  }

  if (targets.length > 0) {
    return targets.map((target) => getHtmlTargets(target as HTMLElement)).flat()
  }

  return [html]
}

/** 미리보기 컨테이너에서 캡처 대상들을 출력 순서대로 모은다. */
export const collectPdfTargets = (container: Element | null | undefined): HTMLElement[] => {
  if (!container) {
    return []
  }

  const targets = Array.from(container.querySelectorAll(".renewal-pdf-container")) as HTMLElement[]
  // 상단 요약은 항상 첫 덩어리다. index 0 이어야 통째로 한 장으로 잡힌다.
  targets.unshift(container.querySelector(".ReportInformationSummaryCont") as HTMLElement)

  return targets
    .map((target, index) => getHtmlTargets(target, index))
    .flat()
    .filter(Boolean)
}

export type ChunkBox = {
  /** html2canvas 에 넘길 캡처 폭 */
  htmlWidth: number
  /** html2canvas 에 넘길 캡처 높이 */
  htmlHeight: number
  isIndividualTable: boolean
}

export type MeasureChunkOptions = {
  /**
   * 상단 요약 높이를 여러 방식으로 재서 가장 큰 값을 쓴다(최소 200px).
   * trend 에서만 쓰던 방어 코드로, 요약이 0 이나 너무 짧게 잡히는 경우를 막는다.
   * daily 는 쓰지 않는다 — 켜면 요약 덩어리의 높이가 달라진다.
   */
  robustSummaryHeight?: boolean
}

/** 캡처에 쓸 요소 박스. html2canvas 의 width/height 와 mm 환산이 같은 값을 봐야 한다. */
export const measureChunkBox = (
  html: HTMLElement,
  containerWidth: number,
  options: MeasureChunkOptions = {},
): ChunkBox => {
  const isIndividualTable =
    html.classList.contains("extracted-individual-table") || html.tagName === "TABLE"

  if (isIndividualTable) {
    return { htmlWidth: html.scrollWidth, htmlHeight: html.scrollHeight, isIndividualTable }
  }

  if (options.robustSummaryHeight && html.classList.contains("ReportInformationSummaryCont")) {
    const rect = html.getBoundingClientRect()
    const computedStyle = window.getComputedStyle(html)
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0
    const marginTop = parseFloat(computedStyle.marginTop) || 0
    const marginBottom = parseFloat(computedStyle.marginBottom) || 0
    const totalPadding = paddingTop + paddingBottom + marginTop + marginBottom

    const childrenHeight = Array.from(html.children).reduce(
      (acc, child) => acc + child.getBoundingClientRect().height,
      0,
    )

    return {
      htmlWidth: containerWidth,
      htmlHeight: Math.max(
        html.scrollHeight,
        rect.height,
        html.offsetHeight,
        childrenHeight + totalPadding,
        200,
      ),
      isIndividualTable,
    }
  }

  if (html.classList.contains("section") || html.classList.contains("renewal-pdf-container")) {
    return { htmlWidth: containerWidth, htmlHeight: html.scrollHeight, isIndividualTable }
  }

  return { htmlWidth: html.scrollWidth, htmlHeight: html.scrollHeight, isIndividualTable }
}

/**
 * 덩어리가 출력물에서 차지할 크기(mm).
 *
 * 표는 실측 폭 비례로 환산한다 — 전폭(190mm) 고정 시 컬럼 적은 분할 테이블이 늘어난다(WEB-2276).
 * 캡처 배율(scale)은 폭·높이에 같이 곱해지므로 여기서는 상쇄되어 영향이 없다.
 */
export const chunkImageSizeMm = (
  box: ChunkBox,
  containerWidth: number,
  contentWidthMm: number,
): { imgWidth: number; imgHeight: number } => {
  const imgWidth = box.isIndividualTable
    ? Math.min(contentWidthMm, Math.round((contentWidthMm * box.htmlWidth) / containerWidth))
    : contentWidthMm

  return { imgWidth, imgHeight: Math.floor((box.htmlHeight * imgWidth) / box.htmlWidth) }
}

/** 미리보기 상태 그대로 각 덩어리의 출력 높이(mm)를 잰다 — 캡처 없이 페이지 배치를 계산할 때 쓴다. */
export const measureChunkHeightsMm = (
  targets: HTMLElement[],
  containerWidth: number,
  contentWidthMm: number,
  options: MeasureChunkOptions = {},
): number[] =>
  targets.map(
    (target) =>
      chunkImageSizeMm(
        measureChunkBox(target, containerWidth, options),
        containerWidth,
        contentWidthMm,
      ).imgHeight,
  )

/**
 * 덩어리의 컴포넌트 종류. 클래스만 보고 판정한다.
 *   Custom Table -> CUSTOM / 그 밖의 표 -> STATS (둘 다 표끼리 페이지를 채운다)
 *   템플릿 이름 · 상단 요약 -> HEADER(1/6)
 *   나머지(Chart by Player · Session Details · Scatter · 액션맵) -> CHART(1/3)
 *
 * 캡처 뒤(ImgData)에도 그대로 쓰려면 요소를 다시 찾을 수 없으므로, 캡처 시점에 판정해
 * ImgData 에 실어 보낸다.
 */
export const chunkTypeOf = (el: HTMLElement): ChunkType => {
  if (el.closest(".CustomTablePdfCont")) {
    return CHUNK_TYPE.CUSTOM
  }

  if (el.tagName === "TABLE" || el.classList.contains("extracted-individual-table")) {
    return CHUNK_TYPE.STATS
  }

  // 액션맵은 코트+표를 한 페이지에 나란히 놓는다 (표가 길면 페이지를 이어 쓴다)
  if (el.closest(".PrintableTacticMapSection")) {
    return CHUNK_TYPE.ACTION_MAP
  }

  if (
    el.classList.contains("ReportInformationSummaryCont") ||
    el.classList.contains("DailyReportSummaryCont")
  ) {
    return CHUNK_TYPE.HEADER
  }

  return CHUNK_TYPE.CHART
}
